import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";

const LABELS = {
  install: "Install",
  onboarding_step_completed: "Onboarding step",
  first_save: "First save",
  save_count_8: "Reached 8 saves",
  cap_hit: "Hit 10-save cap",
  upgrade_click: "Clicked upgrade",
  upgrade_completed: "Upgraded",
};

const RANGES = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "All time", value: null },
];

const pct = (a, b) => (b ? `${Math.min(100, Math.round((a / b) * 100))}%` : "—");

const FunnelRow = ({ step, prev, top, max }) => (
  <div className="grid grid-cols-[180px_1fr_90px_90px] items-center gap-4 py-3 border-b border-[rgba(232,160,48,0.08)]" data-testid={`funnel-row-${step.event}`}>
    <span className="text-sm text-[#FDF8EE]">{LABELS[step.event] || step.event}</span>
    <div className="h-6 bg-[rgba(232,160,48,0.06)] relative">
      <div className="h-full bg-[#E8A030] transition-[width] duration-700" style={{ width: `${(step.unique_users / max) * 100}%` }} />
      <span className="absolute inset-y-0 left-2 flex items-center font-mono text-[11px] text-[#0A0600] mix-blend-normal" style={{ color: step.unique_users / max > 0.15 ? "#0A0600" : "#F5ECD0" }}>
        {step.unique_users} {step.unique_users === 1 ? "user" : "users"} · {step.count} {step.count === 1 ? "event" : "events"}
      </span>
    </div>
    <span className="font-mono text-[12px] text-[#7AB8A0] text-right" data-testid={`funnel-step-conv-${step.event}`}>{prev ? pct(step.unique_users, prev.unique_users) : "—"}</span>
    <span className="font-mono text-[12px] text-[#E6D9BF] text-right">{pct(step.unique_users, top.unique_users)}</span>
  </div>
);

const Stat = ({ label, value, testId }) => (
  <div className="border border-[rgba(232,160,48,0.12)] p-4" data-testid={testId}>
    <p className="font-serif text-2xl font-black text-[#FDF8EE]">{value}</p>
    <p className="font-mono text-[10px] text-[#D9C8A6] uppercase tracking-[1px] mt-1">{label}</p>
  </div>
);

const AskRyanSection = ({ stats }) => {
  const steps = stats.trial_steps;
  const max = Math.max(...steps.map(s => s.unique_users), 1);
  const sig = stats.signal;
  return (
    <div className="border border-[rgba(232,160,48,0.15)] bg-[rgba(232,160,48,0.02)] p-6 space-y-6" data-testid="funnel-ask-ryan">
      <div>
        <h2 className="font-serif text-lg font-bold text-[#FDF8EE]">Ask Ryan free trial</h2>
        <p className="text-sm text-[#E6D9BF] mt-1">Free users reaching each of their 5 free questions, and the gate.</p>
      </div>
      <div>
        {steps.map((st, i) => (
          <div key={st.label} className="grid grid-cols-[180px_1fr_90px] items-center gap-4 py-2.5 border-b border-[rgba(232,160,48,0.08)]" data-testid={`funnel-trial-step-${i + 1}`}>
            <span className="text-sm text-[#FDF8EE]">{st.label}</span>
            <div className="h-5 bg-[rgba(232,160,48,0.06)]">
              <div className="h-full bg-[#A08CC8] transition-[width] duration-700" style={{ width: `${(st.unique_users / max) * 100}%` }} />
            </div>
            <span className="font-mono text-[12px] text-[#E6D9BF] text-right">{st.unique_users} {st.unique_users === 1 ? "user" : "users"}</span>
          </div>
        ))}
      </div>
      <div>
        <h3 className="font-mono text-[11px] text-[#F5ECD0] uppercase tracking-[1px] mb-3">Signal usage this month (limit {sig.limit})</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Signal subscribers" value={sig.subscribers} testId="funnel-signal-subscribers" />
          <Stat label="Past 20 queries" value={sig.past_20} testId="funnel-signal-past-20" />
          <Stat label={`At warning (${Math.round(sig.limit * 0.8)}+)`} value={sig.at_warning} testId="funnel-signal-warning" />
          <Stat label={`At cap (${sig.limit})`} value={sig.subscribers ? `${sig.at_cap} · ${Math.round((sig.at_cap / sig.subscribers) * 100)}%` : sig.at_cap} testId="funnel-signal-cap" />
          <Stat label="Limit-hit events" value={sig.limit_hit_events} testId="funnel-signal-limit-hits" />
        </div>
        <p className="text-[12px] text-[#D9C8A6] mt-3">Review rule: if more than ~20% of Signal users hit the cap, raise it. If almost nobody gets past 20, the limit isn't the constraint.</p>
      </div>
    </div>
  );
};

const Funnel = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/events/funnel`, { params: days ? { days } : {} })
      .then(res => { setData(res.data); setError(null); })
      .catch(e => setError(e.response?.status === 404 ? "notfound" : "Failed to load funnel."));
  }, [days]);

  const funnel = data?.funnel || [];
  const max = Math.max(...funnel.map(f => f.unique_users), 1);

  if (error === "notfound") {
    return (
      <div className="min-h-screen bg-[#0A0600] flex flex-col items-center justify-center gap-4" data-testid="funnel-not-found">
        <p className="font-serif text-5xl font-black text-[#FDF8EE]">404</p>
        <p className="text-sm text-[#E6D9BF]">Page not found.</p>
        <Link to="/" className="font-mono text-[10px] text-[#E8A030] uppercase tracking-[1px] hover:underline" data-testid="funnel-not-found-home">Back home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0600]" data-testid="funnel-page">
      <header className="h-[56px] border-b border-[rgba(232,160,48,0.1)] bg-[#1C1004] flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-[9px] h-[9px] bg-[#E8A030] rotate-45" />
            <span className="font-serif font-black text-lg text-[#FDF8EE]">Ryan</span>
          </Link>
          <span className="font-mono text-[10px] text-[#D9C8A6] uppercase tracking-[1px]">/ Conversion Funnel</span>
        </div>
        <Link to="/dashboard" className="font-mono text-[9px] text-[#8C7A5A] hover:text-[#E8A030] uppercase tracking-[1px] transition-colors" data-testid="funnel-back-link">Back to Dashboard</Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-serif text-3xl font-black text-[#FDF8EE] tracking-[-1px]">Install → Paid</h1>
            <p className="text-sm text-[#E6D9BF] mt-2">Unique users reaching each step, with step-to-step conversion.</p>
          </div>
          <div className="flex gap-1" data-testid="funnel-range">
            {RANGES.map(r => (
              <button key={r.label} onClick={() => setDays(r.value)} data-testid={`funnel-range-${r.value || "all"}`}
                className={`font-mono text-[10px] uppercase tracking-[1px] px-3 py-1.5 border transition-colors ${days === r.value ? "border-[#E8A030] text-[#E8A030] bg-[rgba(232,160,48,0.08)]" : "border-[rgba(232,160,48,0.15)] text-[#8C7A5A] hover:text-[#E8A030]"}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {error ? (
          <p className="text-[#C8600A] text-sm" data-testid="funnel-error">{error}</p>
        ) : !data ? (
          <div className="spinner-ember" />
        ) : (
          <div className="border border-[rgba(232,160,48,0.15)] bg-[rgba(232,160,48,0.02)] p-6" data-testid="funnel-chart">
            <div className="grid grid-cols-[180px_1fr_90px_90px] gap-4 pb-2 font-mono text-[10px] text-[#D9C8A6] uppercase tracking-[1px]">
              <span>Step</span><span>Users</span><span className="text-right">From prev</span><span className="text-right">From install</span>
            </div>
            {funnel.map((step, i) => (
              <FunnelRow key={step.event} step={step} prev={i > 0 ? funnel[i - 1] : null} top={funnel[0]} max={max} />
            ))}
            <p className="font-mono text-[11px] text-[#D9C8A6] mt-4" data-testid="funnel-total">{data.total_events} total events</p>
          </div>
        )}
        {data?.ask_ryan && <AskRyanSection stats={data.ask_ryan} />}
      </main>
    </div>
  );
};

export default Funnel;
